import type { EconomyState } from '../core/economy';
import { getResult, isUnlocked, type ProgressData } from '../core/progress';
import type { LevelEntry } from '../game/registry';
import { sprite } from './sprites';

const MARGIN = 70;

const COLORS = {
  done: '#f2c94c',
  unlocked: '#9bbc0f',
  locked: '#3b4a6a',
  selectRing: '#ffffff',
  text: '#ffffff',
  shadow: 'rgba(15,15,27,0.45)',
};

/** Per-zone level map: themed background + a path of the zone's levels. `levels` are the
 *  levels of the current zone (in order); `selected` indexes into them. */
export function renderOverworld(
  ctx: CanvasRenderingContext2D,
  viewW: number,
  viewH: number,
  levels: LevelEntry[],
  progress: ProgressData,
  selected: number,
  economy: EconomyState,
  zoneName: string,
  theme: string,
): void {
  const ids = levels.map((l) => l.id);

  // Themed background scene.
  const bg = sprite(`bg_${theme}`);
  if (bg) ctx.drawImage(bg, 0, 0, viewW, viewH);
  else {
    ctx.fillStyle = '#13162b';
    ctx.fillRect(0, 0, viewW, viewH);
  }
  // Darken slightly so nodes/text read.
  ctx.fillStyle = COLORS.shadow;
  ctx.fillRect(0, 0, viewW, viewH);

  const n = levels.length;
  const pos = (i: number) => ({
    sx: MARGIN + ((i + 0.5) / n) * (viewW - 2 * MARGIN),
    sy: viewH * 0.52 + Math.sin(i * 1.25) * viewH * 0.16,
  });

  // Path.
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 3;
  ctx.beginPath();
  levels.forEach((_, i) => {
    const { sx, sy } = pos(i);
    if (i === 0) ctx.moveTo(sx, sy);
    else ctx.lineTo(sx, sy);
  });
  ctx.stroke();

  // Nodes.
  levels.forEach((l, i) => {
    const { sx, sy } = pos(i);
    const done = getResult(progress, l.id).completed;
    const unlocked = isUnlocked(progress, ids, l.id);
    ctx.beginPath();
    ctx.arc(sx, sy, 11, 0, Math.PI * 2);
    ctx.fillStyle = done ? COLORS.done : unlocked ? COLORS.unlocked : COLORS.locked;
    ctx.fill();
    if (i === selected) {
      ctx.strokeStyle = COLORS.selectRing;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.fillStyle = '#0f0f1b';
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(i + 1), sx, sy);
  });

  // HUD.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = COLORS.text;
  ctx.font = '14px monospace';
  ctx.fillText(zoneName.toUpperCase(), 16, 12);

  ctx.font = '12px monospace';
  ctx.textAlign = 'right';
  ctx.fillText(`● ${economy.wallet.coins}   ◆ ${economy.wallet.diamonds}`, viewW - 12, 13);
  ctx.textAlign = 'left';

  const sel = levels[selected];
  const locked = !isUnlocked(progress, ids, sel.id);
  ctx.font = '10px monospace';
  ctx.fillText(`> ${sel.name}${locked ? '  (locked)' : ''}`, 16, viewH - 32);
  ctx.fillText('←/→ choose   Space start   Shift shop   Esc zones', 16, viewH - 18);
}
